/**
 * Console shell behaviour: the floor selector, the mobile floor chip, the
 * Tiles/Rows toggle, and ⌘K.
 *
 * The shell renders server-side at the default floor — it has to, because the
 * public routes are edge-cached and one cached body serves everyone (see
 * cache_middleware). This module is what reconciles that default with the floor
 * the visitor actually chose, and it is the only place the selector talks to
 * floor-state.js.
 *
 * Every selector on the page is driven from the same state rather than from its
 * own clicks, so a page can render two of them (top bar and a pane card) and
 * they cannot disagree.
 */

import {
  FLOORS,
  FLOOR_NAMES,
  floorProse,
  getFloor,
  setFloor,
  onFloorChange,
} from './floor-state.js';
import { getView, setView, onViewChange } from './console-view.js';

/** Repaint one selector to match the floor in force. */
function paintSelector(group, floor) {
  const buttons = [...group.querySelectorAll('[data-floor]')];
  for (const button of buttons) {
    const isCurrent = button.dataset.floor === floor;
    button.setAttribute('aria-checked', isCurrent ? 'true' : 'false');
    // Roving tabindex: the group is one Tab stop, arrows move inside it.
    button.tabIndex = isCurrent ? 0 : -1;
  }
  // A group may carry one line of fine print per row, of which only the active
  // row's is shown -- see console_floorgroup.html. Rendering all four and
  // hiding three keeps the sentence next to the row it belongs to in the DOM,
  // which is what a screen reader follows.
  for (const sub of group.querySelectorAll('[data-floor-sub]')) {
    sub.hidden = sub.dataset.floorSub !== floor;
  }
}

/**
 * Rewrite the sentences that state the floor.
 *
 * The Feed hero says the thresholds out loud, and public pages are edge-cached
 * so the server can only ever render the default. `[data-floor-prose]` marks
 * the spans that have to catch up; the strings come from floor-state.js, whose
 * server twin rendered the markup, so this can only ever restate the same
 * sentence with different numbers.
 */
function paintProse(floor) {
  const nodes = document.querySelectorAll('[data-floor-prose]');
  if (nodes.length) {
    const prose = floorProse(floor);
    for (const node of nodes) {
      const key = node.dataset.floorProse;
      if (key in prose) node.textContent = prose[key];
    }
  }

  // A meter that states a preset rather than a seller: how far up the three
  // tiers the floor in force sits. Same component as the one on every row, so
  // the pane's card and the rows below it read as the same scale.
  const tier = FLOORS.indexOf(floor);
  for (const meter of document.querySelectorAll('[data-floor-meter]')) {
    meter.className = meter.className.replace(/t[0-3]/, `t${tier}`);
    [...meter.querySelectorAll('i')].forEach((bar, index) => {
      bar.className = tier >= index + 1 ? 'on' : '';
    });
  }
}

/**
 * The hero's proof line: "41,280 listings checked today · 6,140 cleared Strict
 * this week". The first figure is the catalog's and never changes with the
 * floor; the week count is rendered per floor (`data-week-<floor>`) so this
 * swaps the clause without a request. Off has nothing to clear, so the clause
 * goes.
 */
function paintProof(floor) {
  const clause = document.querySelector('.hh-proof [data-proof-floor]');
  if (!clause) return;
  const proof = clause.closest('.hh-proof');
  const raw = proof.dataset[`week${floor.charAt(0).toUpperCase()}${floor.slice(1)}`];
  if (floor === 'off' || raw === undefined || raw === '') {
    clause.hidden = true;
    return;
  }
  clause.hidden = false;
  clause.querySelector('[data-proof-count]').textContent = Number(raw).toLocaleString();
  clause.querySelector('[data-proof-name]').textContent = FLOOR_NAMES[floor];
}

function paintChips(floor) {
  for (const label of document.querySelectorAll('[data-floor-chip-label]')) {
    label.textContent = FLOOR_NAMES[floor];
  }
}

/**
 * Say what changed. The floor re-filters the whole page, so a change that is
 * only visible as a colour on one of four segments is not enough on its own.
 */
function announce(floor) {
  let region = document.getElementById('floorAnnouncer');
  if (!region) {
    region = document.createElement('div');
    region.id = 'floorAnnouncer';
    region.className = 'vh';
    region.setAttribute('aria-live', 'polite');
    document.body.appendChild(region);
  }
  region.textContent = floor === 'off'
    ? 'Trust floor off — showing all sellers.'
    : `Trust floor set to ${FLOOR_NAMES[floor]}.`;
}

function onKeydown(event, group) {
  const buttons = [...group.querySelectorAll('[data-floor]')];
  const index = buttons.indexOf(document.activeElement);
  if (index === -1) return;

  let next = null;
  switch (event.key) {
    case 'ArrowLeft':
    case 'ArrowUp':
      next = (index - 1 + buttons.length) % buttons.length;
      break;
    case 'ArrowRight':
    case 'ArrowDown':
      next = (index + 1) % buttons.length;
      break;
    case 'Home':
      next = 0;
      break;
    case 'End':
      next = buttons.length - 1;
      break;
    default:
      return;
  }

  event.preventDefault();
  // A radiogroup selects on arrow, not on a second keypress -- moving through
  // the presets is the point, and each stop re-filters what is on screen.
  buttons[next].focus();
  setFloor(buttons[next].dataset.floor);
}

function bindSelector(group) {
  group.addEventListener('click', (event) => {
    const button = event.target.closest('[data-floor]');
    if (button && group.contains(button)) setFloor(button.dataset.floor);
  });
  group.addEventListener('keydown', (event) => onKeydown(event, group));
}

/** Repaint one Tiles/Rows toggle to match the view in force. */
function paintViewToggle(group, view) {
  for (const button of group.querySelectorAll('[data-view]')) {
    const isCurrent = button.dataset.view === view;
    button.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
    button.classList.toggle('on', isCurrent);
  }
}

function bindViewToggle(group) {
  group.addEventListener('click', (event) => {
    const button = event.target.closest('[data-view]');
    if (button && group.contains(button)) setView(button.dataset.view);
  });
}

/**
 * The rail's collapse control.
 *
 * The state lives on <html> as `rail-collapsed`, set before first paint by the
 * inline script in base.html; this only toggles it, stores it, and keeps the
 * button's accessible name and `aria-expanded` telling the truth. All of the
 * geometry is CSS — see the EXPANDED RAIL block in console/shell.css.
 *
 * Expanded is the default and collapsing is the opt-in, so the stored value is
 * only ever written, never assumed: a visitor who has not touched this control
 * has no key and gets labels.
 */
function bindRailToggle() {
  const buttons = document.querySelectorAll('[data-rail-toggle]');
  if (!buttons.length) return;

  const paint = () => {
    const collapsed = document.documentElement.classList.contains('rail-collapsed');
    for (const button of buttons) {
      button.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      const label = button.querySelector('[data-rail-toggle-label]');
      if (label) label.textContent = collapsed ? 'Expand navigation' : 'Collapse navigation';
    }
  };

  for (const button of buttons) {
    button.addEventListener('click', () => {
      const collapsed = document.documentElement.classList.toggle('rail-collapsed');
      try {
        localStorage.setItem('uf_rail', collapsed ? 'collapsed' : 'expanded');
      } catch { /* private mode; the choice just does not outlive the page */ }
      paint();
    });
  }

  paint();
}

/** ⌘K / Ctrl+K focuses the command bar, the shortcut the bar advertises. */
function bindCommandShortcut() {
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'k' || !(event.metaKey || event.ctrlKey)) return;
    // A page whose own search replaces the top bar's (the hybrid homepage and
    // Trusted Finder hide it) marks that field `data-cmdk`; the shortcut goes
    // there, and to the bar everywhere else.
    const input = [...document.querySelectorAll('[data-cmdk], .top .cmd input, .mtop .cmd input')]
      .find((el) => el.offsetParent !== null);
    if (!input) return;
    event.preventDefault();
    input.focus();
    input.select();
  });
}

/**
 * The top-bar floor chip on the homepage points at the one selector, in the
 * feed. The 80px offset clears the sticky bar; reduced motion jumps instead of
 * gliding, and focus follows so a keyboard user lands on the control.
 */
function bindFloorJump() {
  for (const link of document.querySelectorAll('[data-floor-jump]')) {
    link.addEventListener('click', (event) => {
      const target = document.querySelector(link.getAttribute('href'));
      if (!target) return;
      event.preventDefault();
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const top = target.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
      const group = document.querySelector('.hh-floorsel [aria-checked="true"]');
      if (group) group.focus({ preventScroll: true });
    });
  }
}

/**
 * "⌘K" is a Mac glyph. Elsewhere the hint says "Ctrl K", and on a touch device
 * there is no keyboard to press it on, so it is hidden (CSS, pointer: coarse).
 */
function paintKbdHint() {
  const mac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');
  if (mac) return;
  for (const kbd of document.querySelectorAll('[data-kbd-hint]')) kbd.textContent = 'Ctrl K';
}

function paintAll(floor) {
  for (const group of document.querySelectorAll('[data-floorsel]')) {
    paintSelector(group, floor);
  }
  paintChips(floor);
  paintProse(floor);
  paintProof(floor);
  if (document.body) document.body.dataset.floor = floor;
}

function paintViews(view) {
  for (const group of document.querySelectorAll('[data-viewsel]')) {
    paintViewToggle(group, view);
  }
  // The attribute is what CSS and the renderers both read, so it is set even
  // on a page with no toggle on it.
  if (document.body) document.body.dataset.view = view;
}

function init() {
  const groups = document.querySelectorAll('[data-floorsel]');
  for (const group of groups) bindSelector(group);

  // Reconcile the server-rendered default with the visitor's actual floor. No
  // announcement here: nothing changed from the visitor's point of view, this
  // is the page catching up with what they already chose.
  paintAll(getFloor());

  onFloorChange((floor) => {
    paintAll(floor);
    announce(floor);
  });

  for (const group of document.querySelectorAll('[data-viewsel]')) {
    bindViewToggle(group);
  }
  // Same reconciliation as the floor: the markup ships at the default because
  // the page is cached, and the visitor's stored choice is applied here.
  paintViews(getView());
  onViewChange(paintViews);

  bindRailToggle();
  bindCommandShortcut();
  bindFloorJump();
  paintKbdHint();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

export { FLOORS, paintAll, paintViews };
